import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Dialog, Pagination } from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSearchModal } from "../../Context/SearchModalContext";
import {
  addToCart,
  addToCompareList,
  addToWishList,
  decrementQuantity,
  incrementQuantity,
  removeFromCart,
} from "../../store/actions/action";
import ProductCard from "../Cards/ProductCard";
import ViewModal from "./index";

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", AED: "AED" };
const getCurrencyPrefix = () => {
  const cur = (typeof window !== "undefined" && localStorage.getItem("currency")) || "AED";
  return CURRENCY_SYMBOLS[cur] ? CURRENCY_SYMBOLS[cur] + " " : "AED ";
};

const RecommendedRail = ({ onItemClick }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get("get-recommended", { params: { recommended: true, page: 1, limit: 6 } })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data?.map((el) => ({ ...el, id: el._id })) || [];
        setItems(data);
        setError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && (error || items.length === 0)) return null;

  return (
    <div className="ggp-rec-section">
      <div className="ggp-rec-head">
        <span className="ggp-rec-eyebrow">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 2l2.39 6.96H22l-6.19 4.5 2.36 7.04L12 16.27 5.83 20.5l2.36-7.04L2 8.96h7.61L12 2z" />
          </svg>
          Recommended for You
        </span>
        <span className="ggp-rec-sub">Hand-picked premium picks</span>
      </div>

      <div className="ggp-rec-grid">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="ggp-rec-card ggp-rec-card-skel">
                <div className="ggp-rec-img-skel" />
                <div className="ggp-rec-body-skel">
                  <div className="ggp-rec-line ggp-rec-line-lg" />
                  <div className="ggp-rec-line ggp-rec-line-sm" />
                  <div className="ggp-rec-line ggp-rec-line-md" />
                </div>
              </div>
            ))
          : items.map((p) => {
              const rating = Number(p?.five_start_rating) || 0;
              const img = p?.proimg?.[0]?.src;
              return (
                <button
                  key={p?.id}
                  type="button"
                  className="ggp-rec-card"
                  onClick={() => onItemClick && onItemClick(p)}
                >
                  <div
                    className="ggp-rec-img"
                    style={img ? { backgroundImage: `url(${img})` } : undefined}
                  >
                    {!img && <span className="ggp-rec-img-fallback">★</span>}
                    <div className="ggp-rec-img-shine" aria-hidden="true" />
                  </div>
                  <div className="ggp-rec-info">
                    <div className="ggp-rec-title" title={p?.title}>
                      {p?.title}
                    </div>
                    {p?.brand && (
                      <div className="ggp-rec-brand">{p.brand}</div>
                    )}
                    <div className="ggp-rec-bottom">
                      <div className="ggp-rec-stars" aria-label={`${rating} of 5 stars`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <svg
                            key={n}
                            className={`ggp-rec-star ${
                              n <= Math.round(rating) ? "ggp-rec-star-on" : ""
                            }`}
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            width="11"
                            height="11"
                            aria-hidden="true"
                          >
                            <path d="M12 2l2.39 6.96H22l-6.19 4.5 2.36 7.04L12 16.27 5.83 20.5l2.36-7.04L2 8.96h7.61L12 2z" />
                          </svg>
                        ))}
                      </div>
                      <div className="ggp-rec-price">
                        {getCurrencyPrefix()}
                        {p?.price}
                      </div>
                    </div>
                  </div>
                  <span className="ggp-rec-arrow" aria-hidden="true">→</span>
                </button>
              );
            })}
      </div>
    </div>
  );
};

const RECENT_KEY = "ggp_recent_searches";
const RECENT_LIMIT = 6;

const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const persistRecent = (term) => {
  if (!term?.trim()) return [];
  try {
    const list = loadRecent().filter(
      (t) => t.toLowerCase() !== term.trim().toLowerCase()
    );
    list.unshift(term.trim());
    const trimmed = list.slice(0, RECENT_LIMIT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(trimmed));
    return trimmed;
  } catch {
    return [];
  }
};

const SearchModal = ({
  addToCart,
  addToWishList,
  addToCompareList,
  configs,
  ...props
}) => {
  const {
    isModalOpen,
    closeModal,
    searchLoading,
    searchProductsFunc,
    searchQuery,
    setSearchQuery,
    searchFilter,
    handleSearchPageChange,
    searchedProducts,
  } = useSearchModal();

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [recent, setRecent] = useState(loadRecent);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState({});

  // Top suggestion chips — first 6 categories from configs, de-duped.
  const suggestionCats = useMemo(() => {
    const list = configs?.categories || [];
    const seen = new Set();
    const out = [];
    for (const c of list) {
      const name = c?.name?.trim();
      if (!name || name.toLowerCase() === "all category") continue;
      const k = name.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
      if (out.length >= 6) break;
    }
    return out;
  }, [configs?.categories]);

  // Autofocus on open
  useEffect(() => {
    if (isModalOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isModalOpen]);

  // When user does a meaningful search that returns results, persist the term.
  const trimmedQuery = searchQuery.trim();
  useEffect(() => {
    if (
      !searchLoading &&
      trimmedQuery.length >= 2 &&
      searchedProducts.length > 0
    ) {
      const t = setTimeout(() => setRecent(persistRecent(trimmedQuery)), 600);
      return () => clearTimeout(t);
    }
  }, [searchLoading, searchedProducts.length, trimmedQuery]);

  const totalRecords = Number(searchFilter?.totalRecords) || 0;
  const limit = Number(searchFilter?.limit) || 12;
  const pageCount = Math.max(1, Math.ceil(totalRecords / limit));

  const goToShop = (q) => {
    closeModal();
    setTimeout(() => {
      navigate(q ? `/shop?title=${encodeURIComponent(q)}` : "/shop");
    }, 80);
  };

  const handleViewProduct = (item) => {
    setViewProduct(item);
    setViewOpen(true);
  };

  const handleClearRecent = () => {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {}
    setRecent([]);
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (trimmedQuery) searchProductsFunc(trimmedQuery);
  };

  const addToCartProduct = async (product, qty = 1) => addToCart(product, qty);
  const addToWishListProduct = (product, qty = 1) => addToWishList(product, qty);

  return (
    <Fragment>
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            background: "transparent",
            boxShadow: "none",
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: "96vh", sm: "92vh" },
            borderRadius: { xs: "16px", sm: "20px" },
            overflow: "hidden",
          },
        }}
        BackdropProps={{
          sx: {
            background:
              "radial-gradient(circle at 50% 0%, rgba(212,168,87,0.08), transparent 50%), rgba(0,0,0,0.78)",
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <div className="ggp-search-shell">
          {/* TOP BAR */}
          <form className="ggp-search-top" onSubmit={handleSubmit}>
            <span className="ggp-search-icon" aria-hidden="true">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              ref={inputRef}
              type="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="Search gift cards, brands, categories..."
              className="ggp-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {searchQuery && (
              <button
                type="button"
                className="ggp-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}

            <kbd className="ggp-search-kbd" aria-hidden="true">
              ESC
            </kbd>

            <button
              type="button"
              className="ggp-search-close"
              onClick={closeModal}
              aria-label="Close search"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </form>

          {/* META ROW */}
          <div className="ggp-search-meta">
            {trimmedQuery && !searchLoading && totalRecords > 0 && (
              <div className="ggp-search-count">
                <strong>{totalRecords.toLocaleString()}</strong>{" "}
                {totalRecords === 1 ? "result" : "results"} for{" "}
                <span className="ggp-search-count-q">"{trimmedQuery}"</span>
              </div>
            )}
            {trimmedQuery && searchLoading && (
              <div className="ggp-search-count ggp-search-count-loading">
                <span className="ggp-spinner" />
                Searching for "{trimmedQuery}"...
              </div>
            )}
            {trimmedQuery && totalRecords > 0 && (
              <button
                type="button"
                className="ggp-search-viewall"
                onClick={() => goToShop(trimmedQuery)}
              >
                View all in Shop →
              </button>
            )}
          </div>

          {/* BODY */}
          <div className="ggp-search-body">
            {/* EMPTY STATE — no query yet */}
            {!trimmedQuery && (
              <div className="ggp-search-empty">
                {recent.length > 0 && (
                  <div className="ggp-search-section">
                    <div className="ggp-search-section-head">
                      <span>Recent Searches</span>
                      <button
                        type="button"
                        className="ggp-search-section-clear"
                        onClick={handleClearRecent}
                      >
                        Clear
                      </button>
                    </div>
                    <div className="ggp-chip-row">
                      {recent.map((term, i) => (
                        <button
                          key={i}
                          type="button"
                          className="ggp-search-chip"
                          onClick={() => setSearchQuery(term)}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {suggestionCats.length > 0 && (
                  <div className="ggp-search-section">
                    <div className="ggp-search-section-head">
                      <span>Popular Categories</span>
                    </div>
                    <div className="ggp-chip-row">
                      {suggestionCats.map((cat) => (
                        <button
                          key={cat?._id || cat?.name}
                          type="button"
                          className="ggp-search-chip ggp-search-chip-cat"
                          onClick={() => goToShop(cat?.name)}
                        >
                          {cat?.url && (
                            <img
                              src={cat.url}
                              alt=""
                              className="ggp-search-chip-img"
                            />
                          )}
                          {cat?.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <RecommendedRail
                  onItemClick={(p) => {
                    closeModal();
                    setTimeout(() => navigate(`/product-single/${p.slug}`), 80);
                  }}
                />

                <div className="ggp-search-tip">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  Tip: results update as you type. Press{" "}
                  <kbd>Enter</kbd> to refresh or <kbd>Esc</kbd> to close.
                </div>
              </div>
            )}

            {/* LOADING SKELETON */}
            {trimmedQuery && searchLoading && (
              <div className="ggp-search-grid">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="ggp-skel-card">
                    <div className="ggp-skel-img" />
                    <div className="ggp-skel-line ggp-skel-line-lg" />
                    <div className="ggp-skel-line" />
                    <div className="ggp-skel-line ggp-skel-line-sm" />
                  </div>
                ))}
              </div>
            )}

            {/* NO RESULTS */}
            {trimmedQuery && !searchLoading && searchedProducts.length === 0 && (
              <div className="ggp-search-noresults">
                <div className="ggp-search-noresults-icon">
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </div>
                <h3>No results for "{trimmedQuery}"</h3>
                <p>Try a different keyword, brand, or category.</p>
                <button
                  type="button"
                  className="ggp-search-noresults-btn"
                  onClick={() => goToShop("")}
                >
                  Browse all gift cards
                </button>
              </div>
            )}

            {/* RESULTS */}
            {trimmedQuery && !searchLoading && searchedProducts.length > 0 && (
              <>
                <div className="ggp-search-grid">
                  {searchedProducts.map((product, index) => (
                    <ProductCard
                      product={product}
                      key={product?.id || index}
                      addToWishListProduct={addToWishListProduct}
                      addToCartProduct={addToCartProduct}
                      carts={props.carts}
                      handleClickOpen={handleViewProduct}
                      className="col-xl-12 col-lg-12 col-sm-12 col-12"
                      {...props}
                    />
                  ))}
                </div>
                {pageCount > 1 && (
                  <div className="ggp-search-pagination">
                    <Pagination
                      count={pageCount}
                      size="medium"
                      page={searchFilter?.page || 1}
                      onChange={handleSearchPageChange}
                      siblingCount={1}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <style>{`
          .ggp-search-shell {
            background: linear-gradient(180deg, #15110a 0%, #0b0b0c 100%);
            border: 1px solid rgba(212, 168, 87, 0.25);
            border-radius: inherit;
            color: #fff;
            display: flex;
            flex-direction: column;
            min-height: min(70vh, 600px);
            max-height: inherit;
            overflow: hidden;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          }

          /* TOP BAR */
          .ggp-search-top {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 14px 18px;
            border-bottom: 1px solid rgba(212, 168, 87, 0.15);
            background: linear-gradient(180deg, #1a1208 0%, #15110a 100%);
            flex-shrink: 0;
          }
          .ggp-search-icon {
            color: #d4a857;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .ggp-search-input {
            flex: 1;
            background: transparent;
            border: none;
            color: #fff;
            font-size: 17px;
            padding: 6px 4px;
            outline: none;
            min-width: 0;
            font-family: inherit;
          }
          .ggp-search-input::placeholder { color: #6b7280; }
          .ggp-search-input::-webkit-search-cancel-button { display: none; }

          .ggp-search-clear {
            background: transparent;
            border: none;
            color: #9aa0a8;
            font-size: 22px;
            cursor: pointer;
            width: 28px;
            height: 28px;
            line-height: 1;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .ggp-search-clear:hover {
            background: rgba(212, 168, 87, 0.15);
            color: #d4a857;
          }
          .ggp-search-kbd {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 1px;
            color: #d4a857;
            background: rgba(212, 168, 87, 0.1);
            border: 1px solid rgba(212, 168, 87, 0.3);
            border-radius: 5px;
            padding: 3px 7px;
            line-height: 1;
          }
          .ggp-search-close {
            background: transparent;
            border: 1px solid rgba(212, 168, 87, 0.3);
            color: #d4a857;
            width: 36px;
            height: 36px;
            border-radius: 10px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background .2s ease, color .2s ease;
            flex-shrink: 0;
          }
          .ggp-search-close:hover {
            background: linear-gradient(135deg, #d4a857, #b58a3f);
            color: #1a1208;
            border-color: transparent;
          }

          /* META */
          .ggp-search-meta {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            padding: 10px 18px;
            border-bottom: 1px solid rgba(212, 168, 87, 0.1);
            background: #0f0e0c;
            min-height: 18px;
            flex-shrink: 0;
          }
          .ggp-search-count {
            font-size: 13px;
            color: #9aa0a8;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .ggp-search-count strong { color: #d4a857; font-weight: 700; }
          .ggp-search-count-q {
            color: #fff;
            font-style: italic;
          }
          .ggp-search-count-loading { color: #d4a857; }
          .ggp-search-viewall {
            background: transparent;
            border: none;
            color: #d4a857;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            padding: 4px 8px;
            border-radius: 6px;
          }
          .ggp-search-viewall:hover {
            background: rgba(212, 168, 87, 0.12);
          }

          .ggp-spinner {
            width: 14px;
            height: 14px;
            border-radius: 50%;
            border: 2px solid rgba(212, 168, 87, 0.3);
            border-top-color: #d4a857;
            animation: ggp-spin 0.7s linear infinite;
            display: inline-block;
          }
          @keyframes ggp-spin {
            to { transform: rotate(360deg); }
          }

          /* BODY */
          .ggp-search-body {
            flex: 1;
            overflow-y: auto;
            padding: 22px;
            background: #0b0b0c;
          }
          .ggp-search-body::-webkit-scrollbar { width: 8px; }
          .ggp-search-body::-webkit-scrollbar-thumb {
            background: rgba(212, 168, 87, 0.25);
            border-radius: 999px;
          }
          .ggp-search-body::-webkit-scrollbar-thumb:hover {
            background: #d4a857;
          }

          /* EMPTY STATE */
          .ggp-search-empty {
            display: flex;
            flex-direction: column;
            gap: 22px;
            max-width: 760px;
            margin: 0 auto;
            padding: 8px 4px 12px;
          }
          .ggp-search-section { display: flex; flex-direction: column; gap: 10px; }
          .ggp-search-section-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 11px;
            color: #9aa0a8;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            font-weight: 700;
          }
          .ggp-search-section-clear {
            background: transparent;
            border: none;
            color: #9aa0a8;
            font-size: 11px;
            cursor: pointer;
            text-decoration: underline;
            text-transform: none;
            letter-spacing: 0;
          }
          .ggp-search-section-clear:hover { color: #d4a857; }

          .ggp-chip-row {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }
          .ggp-search-chip {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            background: rgba(255, 255, 255, 0.04);
            border: 1px solid rgba(212, 168, 87, 0.25);
            color: #e6e8ec;
            padding: 7px 14px;
            border-radius: 999px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: background .2s ease, border-color .2s ease, color .2s ease;
            font-family: inherit;
          }
          .ggp-search-chip:hover {
            background: rgba(212, 168, 87, 0.18);
            border-color: #d4a857;
            color: #fff;
          }
          .ggp-search-chip-cat {
            padding: 6px 14px 6px 8px;
          }
          .ggp-search-chip-img {
            width: 18px;
            height: 18px;
            border-radius: 4px;
            object-fit: cover;
            background: rgba(255,255,255,0.08);
          }

          .ggp-search-tip {
            margin-top: 4px;
            font-size: 12px;
            color: #6b7280;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 12px;
            border-top: 1px dashed rgba(212, 168, 87, 0.18);
            padding-top: 16px;
          }
          .ggp-search-tip kbd {
            font-family: inherit;
            font-size: 10px;
            background: rgba(212, 168, 87, 0.1);
            color: #d4a857;
            border: 1px solid rgba(212, 168, 87, 0.25);
            border-radius: 4px;
            padding: 2px 5px;
            margin: 0 2px;
            line-height: 1;
          }

          /* GRID */
          .ggp-search-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 18px;
          }

          /* SKELETON */
          .ggp-skel-card {
            background: #15110a;
            border: 1px solid rgba(212, 168, 87, 0.12);
            border-radius: 12px;
            padding: 14px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            min-height: 320px;
          }
          .ggp-skel-img {
            height: 180px;
            border-radius: 8px;
            background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
            background-size: 200% 100%;
            animation: ggp-shimmer 1.4s linear infinite;
          }
          .ggp-skel-line {
            height: 12px;
            border-radius: 4px;
            background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
            background-size: 200% 100%;
            animation: ggp-shimmer 1.4s linear infinite;
          }
          .ggp-skel-line-lg { height: 16px; width: 80%; }
          .ggp-skel-line-sm { width: 40%; }
          @keyframes ggp-shimmer {
            from { background-position: 200% 0; }
            to { background-position: -200% 0; }
          }

          /* NO RESULTS */
          .ggp-search-noresults {
            text-align: center;
            padding: 48px 20px;
            max-width: 480px;
            margin: 0 auto;
          }
          .ggp-search-noresults-icon {
            color: #d4a857;
            margin-bottom: 16px;
            display: flex;
            justify-content: center;
          }
          .ggp-search-noresults h3 {
            font-size: 18px;
            color: #fff;
            margin: 0 0 6px;
            font-weight: 700;
          }
          .ggp-search-noresults p {
            color: #9aa0a8;
            font-size: 13px;
            margin: 0 0 20px;
          }
          .ggp-search-noresults-btn {
            background: linear-gradient(135deg, #d4a857, #b58a3f);
            color: #1a1208;
            border: none;
            padding: 10px 22px;
            border-radius: 10px;
            font-weight: 700;
            font-size: 13px;
            cursor: pointer;
            transition: filter .2s ease, transform .2s ease;
            font-family: inherit;
          }
          .ggp-search-noresults-btn:hover {
            filter: brightness(1.05);
            transform: translateY(-1px);
          }

          /* PAGINATION */
          .ggp-search-pagination {
            display: flex;
            justify-content: center;
            padding: 24px 0 8px;
          }
          .ggp-search-pagination .MuiPaginationItem-root {
            color: #cfd2d6;
          }
          .ggp-search-pagination .MuiPaginationItem-root:hover {
            background: rgba(212, 168, 87, 0.15);
          }
          .ggp-search-pagination .MuiPaginationItem-root.Mui-selected {
            background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
            color: #1a1208 !important;
            font-weight: 700;
          }
          .ggp-search-pagination .MuiPaginationItem-root.Mui-selected:hover {
            background: linear-gradient(135deg, #f1cf85, #d4a857) !important;
          }

          /* RECOMMENDED RAIL */
          .ggp-rec-section {
            display: flex;
            flex-direction: column;
            gap: 14px;
            padding-top: 8px;
            border-top: 1px solid rgba(212, 168, 87, 0.15);
            margin-top: 4px;
          }
          .ggp-rec-head {
            display: flex;
            align-items: baseline;
            gap: 12px;
            flex-wrap: wrap;
          }
          .ggp-rec-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #d4a857;
          }
          .ggp-rec-eyebrow svg {
            color: #d4a857;
            filter: drop-shadow(0 0 6px rgba(212, 168, 87, 0.5));
          }
          .ggp-rec-sub {
            font-size: 12px;
            color: #6b7280;
            font-style: italic;
          }

          .ggp-rec-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }

          .ggp-rec-card {
            position: relative;
            display: flex;
            align-items: stretch;
            gap: 12px;
            background: linear-gradient(135deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212, 168, 87, 0.18);
            border-radius: 12px;
            padding: 10px;
            cursor: pointer;
            text-align: left;
            font-family: inherit;
            color: inherit;
            overflow: hidden;
            transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease;
            min-height: 96px;
          }
          .ggp-rec-card:hover {
            transform: translateY(-2px);
            border-color: rgba(212, 168, 87, 0.55);
            box-shadow: 0 10px 24px rgba(212, 168, 87, 0.12), 0 4px 10px rgba(0, 0, 0, 0.4);
          }
          .ggp-rec-card:hover .ggp-rec-arrow {
            opacity: 1;
            transform: translateX(0);
          }
          .ggp-rec-card:hover .ggp-rec-img-shine {
            transform: translateX(180%);
          }

          .ggp-rec-img {
            position: relative;
            width: 78px;
            height: 78px;
            flex-shrink: 0;
            border-radius: 10px;
            background-color: #0b0b0c;
            background-size: cover;
            background-position: center;
            border: 1px solid rgba(212, 168, 87, 0.18);
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .ggp-rec-img-fallback {
            font-size: 22px;
            color: rgba(212, 168, 87, 0.4);
          }
          .ggp-rec-img-shine {
            position: absolute;
            inset: 0;
            background: linear-gradient(120deg,
              transparent 30%,
              rgba(255, 255, 255, 0.18) 50%,
              transparent 70%);
            transform: translateX(-180%);
            transition: transform .8s ease;
            pointer-events: none;
          }

          .ggp-rec-info {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .ggp-rec-title {
            font-size: 13px;
            font-weight: 600;
            color: #fff;
            line-height: 1.35;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .ggp-rec-brand {
            font-size: 11px;
            color: #9aa0a8;
            text-transform: uppercase;
            letter-spacing: 0.6px;
          }
          .ggp-rec-bottom {
            margin-top: auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding-top: 4px;
          }
          .ggp-rec-stars {
            display: inline-flex;
            gap: 1px;
          }
          .ggp-rec-star { color: #2a2d33; }
          .ggp-rec-star-on { color: #d4a857; }

          .ggp-rec-price {
            font-size: 13px;
            font-weight: 700;
            color: #d4a857;
            letter-spacing: 0.3px;
            white-space: nowrap;
          }

          .ggp-rec-arrow {
            position: absolute;
            top: 10px;
            right: 12px;
            color: #d4a857;
            font-size: 14px;
            opacity: 0;
            transform: translateX(-4px);
            transition: opacity .25s ease, transform .25s ease;
          }

          .ggp-rec-card-skel {
            cursor: default;
          }
          .ggp-rec-img-skel {
            width: 78px;
            height: 78px;
            border-radius: 10px;
            background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
            background-size: 200% 100%;
            animation: ggp-shimmer 1.4s linear infinite;
            flex-shrink: 0;
          }
          .ggp-rec-body-skel {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 6px;
            justify-content: center;
          }
          .ggp-rec-line {
            height: 10px;
            border-radius: 4px;
            background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
            background-size: 200% 100%;
            animation: ggp-shimmer 1.4s linear infinite;
          }
          .ggp-rec-line-lg { width: 80%; height: 12px; }
          .ggp-rec-line-md { width: 50%; }
          .ggp-rec-line-sm { width: 30%; height: 9px; }

          /* RESPONSIVE */
          @media (max-width: 991px) {
            .ggp-search-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
          }
          @media (max-width: 575px) {
            .ggp-search-top { padding: 12px 14px; gap: 8px; }
            .ggp-search-input { font-size: 15px; }
            .ggp-search-kbd { display: none; }
            .ggp-search-meta { padding: 8px 14px; }
            .ggp-search-count { font-size: 12px; }
            .ggp-search-body { padding: 16px; }
            .ggp-search-grid { grid-template-columns: 1fr; }
            .ggp-skel-card { min-height: 240px; }
            .ggp-skel-img { height: 140px; }
            .ggp-rec-grid { grid-template-columns: 1fr; }
            .ggp-rec-img, .ggp-rec-img-skel { width: 64px; height: 64px; }
            .ggp-rec-title { font-size: 12px; }
            .ggp-rec-arrow { display: none; }
          }
        `}</style>
      </Dialog>

      <ViewModal
        addToCartProduct={addToCartProduct}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        product={viewProduct}
        carts={props.carts}
        {...props}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  carts: state.cartList.cart,
  w_list: state.wishList.w_list,
  configs: state.configs,
});

export default connect(mapStateToProps, {
  addToCart,
  addToWishList,
  addToCompareList,
  removeFromCart,
  decrementQuantity,
  incrementQuantity,
})(SearchModal);
